import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronRight, X } from "lucide-react";
import { navigation, type NavItem } from "../data/navigation";

interface SidebarProps {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

function isPathActive(locationPath: string, itemPath: string) {
  return locationPath === itemPath;
}

function isPathInSection(locationPath: string, itemPath: string) {
  return locationPath.startsWith(itemPath + "/");
}

function hasActiveChild(locationPath: string, item: NavItem): boolean {
  if (!item.children || item.children.length === 0) return false;
  return item.children.some(
    (child) =>
      isPathActive(locationPath, child.path) ||
      isPathInSection(locationPath, child.path) ||
      hasActiveChild(locationPath, child),
  );
}

function NavTreeItem({
  item,
  depth = 0,
  searchQuery,
  locationPath,
  forceExpand,
}: {
  item: NavItem;
  depth?: number;
  searchQuery: string;
  locationPath: string;
  forceExpand?: boolean;
}) {
  const isActive = isPathActive(locationPath, item.path);
  const isSectionActive = isPathInSection(locationPath, item.path);
  const hasChildren = item.children && item.children.length > 0;
  const childIsActive = hasActiveChild(locationPath, item);

  const [userExpanded, setUserExpanded] = useState(
    isSectionActive || isActive || childIsActive,
  );
  const expanded = forceExpand ? true : userExpanded;

  const query = searchQuery.trim().toLowerCase();
  const matchesSelf = item.label.toLowerCase().includes(query);
  const childMatches = hasChildren
    ? item.children!.some((c) => c.label.toLowerCase().includes(query))
    : false;
  const isVisible = query === "" || matchesSelf || childMatches;

  if (!isVisible) return null;

  return (
    <div>
      <div className="flex items-center my-0.5">
        {hasChildren && (
          <button
            onClick={() => setUserExpanded((p) => !p)}
            className="mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-theme-muted hover:text-theme-secondary hover:bg-theme-elevated transition-colors"
          >
            <motion.div
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </motion.div>
          </button>
        )}
        {!hasChildren && <span className="mr-1 h-5 w-5 shrink-0" />}
        <Link
          to={item.path}
          data-active={isActive ? "true" : undefined}
          className={`relative flex-1 truncate rounded-lg py-1.5 px-2.5 text-sm transition-all ${
            isActive
              ? "font-semibold text-primary bg-primary/10"
              : "text-theme-secondary hover:text-theme hover:bg-theme-elevated"
          }`}
        >
          {item.label}
        </Link>
      </div>

      <AnimatePresence initial={false}>
        {hasChildren && expanded && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {item.children!.map((child) => (
              <li key={child.id} className="ml-4 pl-1">
                <NavTreeItem
                  item={child}
                  depth={depth + 1}
                  searchQuery={searchQuery}
                  locationPath={locationPath}
                  forceExpand={forceExpand}
                />
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Sidebar({ mobileOpen, onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const navRef = useRef<HTMLElement>(null);

  const filteredNav = useMemo(() => {
    if (!searchQuery.trim()) return navigation;
    const q = searchQuery.trim().toLowerCase();
    return navigation
      .map((section) => {
        const sectionMatches = section.label.toLowerCase().includes(q);
        const filteredChildren = section.children?.filter((c) =>
          c.label.toLowerCase().includes(q),
        );
        if (sectionMatches) return section;
        if (filteredChildren && filteredChildren.length > 0) {
          return { ...section, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean) as NavItem[];
  }, [searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  // Scroll active sidebar item into view
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const activeLink = nav.querySelector(
      '[data-active="true"]',
    ) as HTMLElement | null;
    if (!activeLink) return;

    const navRect = nav.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    const isAbove = linkRect.top < navRect.top + 8;
    const isBelow = linkRect.bottom > navRect.bottom - 8;

    if (isAbove || isBelow) {
      activeLink.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [location.pathname]);

  const handleLinkClick = useCallback(() => {
    onCloseMobile();
  }, [onCloseMobile]);

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs lg:hidden"
            onClick={onCloseMobile}
          />
        )}
      </AnimatePresence>

      {/* Floating Sidebar */}
      <aside
        className={`fixed inset-y-4 left-4 z-50 w-[270px] transform rounded-2xl border border-theme bg-theme-surface/90 backdrop-blur-md shadow-xl transition-all duration-300 ease-out lg:sticky lg:top-6 lg:inset-y-auto lg:h-[calc(100vh-10.5rem)] lg:mb-6 lg:translate-x-0 ${
          mobileOpen
            ? "translate-x-0"
            : "-translate-x-[calc(100%+2rem)] lg:translate-x-0"
        }`}
      >
        <div className="flex h-full flex-col p-3">
          {/* Mobile Close */}
          <div className="flex items-center justify-between pb-2 lg:hidden border-b border-theme mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-theme-muted px-1">
              Documentation
            </span>
            <button
              onClick={onCloseMobile}
              className="rounded-lg p-1 text-theme-tertiary hover:bg-theme-elevated hover:text-theme"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          <div className="pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-theme-muted" />
              <input
                type="text"
                placeholder="Search docs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-theme bg-theme-body/60 py-2 pl-9 pr-3 text-sm text-theme placeholder:text-theme-muted focus:border-primary/40 focus:outline-none focus:ring-1 focus:ring-primary/20 transition-all"
              />
            </div>
          </div>

          {/* Nav tree */}
          <nav
            ref={navRef}
            className="flex-1 overflow-y-auto pr-1 space-y-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onClick={handleLinkClick}
          >
            <div className="space-y-0.5">
              {filteredNav.map((item) => (
                <NavTreeItem
                  key={item.id}
                  item={item}
                  searchQuery={searchQuery}
                  locationPath={location.pathname}
                  forceExpand={isSearching}
                />
              ))}
              {filteredNav.length === 0 && (
                <p className="px-2 py-4 text-center text-sm text-theme-muted">
                  No results found
                </p>
              )}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
}
